import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Heart, CheckCircle, Sparkles, UserPlus, X, Mail } from 'lucide-react';

const RSVPSection = () => {
  const { language, t, isBangla, hasSelectedLanguage } = useLanguage();
  
  const [formData, setFormData] = useState({
    primary_guest_name: '',
    email: '',
    attending: null,
    bringing_guests: false,
    guest_names: [''],
    notes: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const validateForm = () => {
    if (!formData.primary_guest_name.trim()) {
      setError(t('rsvpErrorName'));
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError(t('rsvpErrorEmail'));
      return false;
    }
    
    if (formData.attending === null) {
      setError(t('rsvpError'));
      return false;
    }
    
    if (formData.attending && formData.bringing_guests) {
      const hasEmptyGuest = formData.guest_names.some(name => !name.trim());
      if (hasEmptyGuest) {
        setError(t('rsvpErrorGuestName'));
        return false;
      }
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);

    try {
      const payload = {
        primary_guest_name: formData.primary_guest_name.trim(),
        email: formData.email.trim(),
        attending: formData.attending,
        bringing_guests: formData.attending && formData.bringing_guests,
        guest_names: formData.attending && formData.bringing_guests 
          ? formData.guest_names.filter(n => n.trim()).map(n => n.trim())
          : [],
        notes: formData.notes.trim() || null,
        language: language
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rsvp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Failed to submit RSVP');
      
      setIsSubmitted(true);
    } catch (err) {
      setError(t('rsvpError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const addGuestField = () => {
    if (formData.guest_names.length < 10) {
      setFormData({ ...formData, guest_names: [...formData.guest_names, ''] });
    }
  };

  const removeGuestField = (index) => {
    const newNames = formData.guest_names.filter((_, i) => i !== index);
    setFormData({ ...formData, guest_names: newNames.length ? newNames : [''] });
  };

  const updateGuestName = (index, value) => {
    const newNames = [...formData.guest_names];
    newNames[index] = value;
    setFormData({ ...formData, guest_names: newNames });
  };

  const resetForm = () => {
    setFormData({
      primary_guest_name: '',
      email: '',
      attending: null,
      bringing_guests: false,
      guest_names: [''],
      notes: ''
    });
    setIsSubmitted(false);
    setError('');
  };

  if (!hasSelectedLanguage) return null;

  return (
    <section 
      id="rsvp" 
      className="py-16 sm:py-20 md:py-28 px-4 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #FFF8F0 0%, #F5E6D3 50%, #FFF8F0 100%)'
      }}
      data-testid="rsvp-section"
    >
      {/* Decorative elements */}
      <div className="absolute top-10 left-4 sm:left-10 w-24 sm:w-32 h-24 sm:h-32 border border-[#D4AF37]/20 rounded-full animate-spin" style={{ animationDuration: '25s' }} />
      <div className="absolute bottom-10 right-4 sm:right-10 w-32 sm:w-48 h-32 sm:h-48 border border-[#800020]/10 rounded-full animate-spin" style={{ animationDuration: '30s', animationDirection: 'reverse' }} />

      <div className="max-w-lg mx-auto relative z-10">
        {/* Chapter Indicator */}
        <motion.div 
          className="text-center mb-6 flex items-center justify-center gap-3"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Sparkles className="w-4 h-4 text-[#D4AF37]" />
          <span className={`text-[#D4AF37] text-sm tracking-[0.2em] sm:tracking-[0.3em] uppercase font-light ${isBangla ? 'font-bangla' : ''}`}>
            {t('chapterThree')}
          </span>
          <Sparkles className="w-4 h-4 text-[#D4AF37]" />
        </motion.div>

        {/* Section Header */}
        <div className="text-center mb-8 sm:mb-10">
          <motion.div 
            className="w-16 sm:w-20 mx-auto h-0.5 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mb-6"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8 }}
          />
          <motion.h2 
            className={`font-heading text-3xl sm:text-4xl md:text-5xl font-semibold text-[#800020] mb-3 ${isBangla ? 'font-bangla' : ''}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            data-testid="rsvp-title"
          >
            {t('rsvpTitle')}
          </motion.h2>
          <motion.p 
            className={`text-base sm:text-lg md:text-xl text-[#2D2424]/70 px-4 ${isBangla ? 'font-bangla leading-relaxed' : ''}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {t('rsvpSubtitle')}
          </motion.p>
          <motion.div 
            className="w-16 sm:w-20 mx-auto h-0.5 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mt-6"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          />
        </div>

        {/* Form or Success Message */}
        <motion.div
          className="bg-white/80 backdrop-blur-sm border border-[#E6C288] rounded-2xl p-5 sm:p-6 md:p-8 shadow-xl"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <AnimatePresence mode="wait">
            {isSubmitted ? (
              // Success State
              <motion.div 
                key="success"
                className="text-center py-6 sm:py-8"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
              >
                <motion.div
                  className="w-16 sm:w-20 h-16 sm:h-20 mx-auto mb-6 bg-gradient-to-br from-[#D4AF37] to-[#B8860B] rounded-full flex items-center justify-center"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
                >
                  <CheckCircle className="w-8 sm:w-10 h-8 sm:h-10 text-white" />
                </motion.div>
                <h3 className={`font-heading text-2xl sm:text-3xl text-[#800020] mb-4 ${isBangla ? 'font-bangla' : ''}`}>
                  {t('rsvpThankYou')}
                </h3>
                <p className={`text-[#2D2424]/70 mb-2 text-sm sm:text-base ${isBangla ? 'font-bangla leading-relaxed' : ''}`}>
                  {formData.attending ? t('rsvpConfirmation') : t('rsvpDeclineMessage')}
                </p>
                {formData.attending && (
                  <p className={`text-[#2D2424]/60 text-sm mb-4 ${isBangla ? 'font-bangla' : ''}`}>
                    {t('rsvpConfirmationEmail')}
                  </p>
                )}
                <motion.div
                  className="mt-6"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  <Heart className="w-6 sm:w-8 h-6 sm:h-8 mx-auto text-[#800020] fill-[#800020] animate-pulse" />
                </motion.div>
                <button
                  onClick={resetForm}
                  className={`mt-6 text-[#800020] underline text-sm hover:text-[#600018] transition-colors ${isBangla ? 'font-bangla' : ''}`}
                  data-testid="submit-another-btn"
                >
                  {t('submitAnotherRsvp')}
                </button>
              </motion.div>
            ) : (
              // Form
              <motion.form 
                key="form"
                onSubmit={handleSubmit} 
                className="space-y-5 sm:space-y-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {/* Primary Guest Name */}
                <div>
                  <label 
                    htmlFor="primary_guest_name" 
                    className={`block text-sm font-medium text-[#800020] mb-2 ${isBangla ? 'font-bangla' : ''}`}
                  >
                    {t('primaryGuestName')} *
                  </label>
                  <input
                    type="text"
                    id="primary_guest_name"
                    data-testid="rsvp-primary-name"
                    required
                    maxLength={100}
                    value={formData.primary_guest_name}
                    onChange={(e) => setFormData({ ...formData, primary_guest_name: e.target.value })}
                    placeholder={t('primaryGuestNamePlaceholder')}
                    className={`w-full px-4 py-3 min-h-[48px] rounded-xl border-2 border-[#E6C288] focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none transition-all bg-white/50 text-[#2D2424] placeholder-[#2D2424]/40 text-base ${isBangla ? 'font-bangla' : ''}`}
                  />
                </div>

                {/* Email */}
                <div>
                  <label 
                    htmlFor="email" 
                    className={`block text-sm font-medium text-[#800020] mb-2 ${isBangla ? 'font-bangla' : ''}`}
                  >
                    {t('emailLabel')} *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#D4AF37]" />
                    <input
                      type="email"
                      id="email"
                      data-testid="rsvp-email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder={t('emailPlaceholder')}
                      className="w-full pl-12 pr-4 py-3 min-h-[48px] rounded-xl border-2 border-[#E6C288] focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none transition-all bg-white/50 text-[#2D2424] placeholder-[#2D2424]/40 text-base"
                    />
                  </div>
                </div>

                {/* Attendance */}
                <div>
                  <label className={`block text-sm font-medium text-[#800020] mb-3 ${isBangla ? 'font-bangla' : ''}`}>
                    {t('attendanceLabel')} *
                  </label>
                  <div className="grid grid-cols-1 gap-3">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, attending: true })}
                      className={`p-4 min-h-[52px] rounded-xl border-2 transition-all text-left flex items-center gap-3 ${
                        formData.attending === true 
                          ? 'border-[#228B22] bg-[#228B22]/10 text-[#228B22]' 
                          : 'border-[#E6C288] hover:border-[#D4AF37] text-[#2D2424]/70'
                      } ${isBangla ? 'font-bangla' : ''}`}
                      data-testid="attendance-yes"
                    >
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        formData.attending === true ? 'border-[#228B22] bg-[#228B22]' : 'border-[#E6C288]'
                      }`}>
                        {formData.attending === true && <div className="w-2 h-2 bg-white rounded-full" />}
                      </div>
                      <span className="text-sm sm:text-base">{t('attendanceYes')}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, attending: false, bringing_guests: false, guest_names: [''] })}
                      className={`p-4 min-h-[52px] rounded-xl border-2 transition-all text-left flex items-center gap-3 ${
                        formData.attending === false 
                          ? 'border-[#CC0000] bg-[#CC0000]/10 text-[#CC0000]' 
                          : 'border-[#E6C288] hover:border-[#D4AF37] text-[#2D2424]/70'
                      } ${isBangla ? 'font-bangla' : ''}`}
                      data-testid="attendance-no"
                    >
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        formData.attending === false ? 'border-[#CC0000] bg-[#CC0000]' : 'border-[#E6C288]'
                      }`}>
                        {formData.attending === false && <div className="w-2 h-2 bg-white rounded-full" />}
                      </div>
                      <span className="text-sm sm:text-base">{t('attendanceNo')}</span>
                    </button>
                  </div>
                </div>

                {/* Bringing Guests - Only show if attending */}
                <AnimatePresence>
                  {formData.attending && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <label className={`block text-sm font-medium text-[#800020] mb-3 ${isBangla ? 'font-bangla' : ''}`}>
                        {t('bringingGuestsLabel')} *
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, bringing_guests: true })}
                          className={`p-4 min-h-[48px] rounded-xl border-2 transition-all text-center ${
                            formData.bringing_guests 
                              ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#800020]' 
                              : 'border-[#E6C288] hover:border-[#D4AF37] text-[#2D2424]/70'
                          } ${isBangla ? 'font-bangla' : ''}`}
                          data-testid="bringing-guests-yes"
                        >
                          {t('bringingGuestsYes')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, bringing_guests: false, guest_names: [''] })}
                          className={`p-4 min-h-[48px] rounded-xl border-2 transition-all text-center ${
                            !formData.bringing_guests 
                              ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#800020]' 
                              : 'border-[#E6C288] hover:border-[#D4AF37] text-[#2D2424]/70'
                          } ${isBangla ? 'font-bangla' : ''}`}
                          data-testid="bringing-guests-no"
                        >
                          {t('bringingGuestsNo')}
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Guest Names - Only show if bringing guests */}
                <AnimatePresence>
                  {formData.attending && formData.bringing_guests && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <label className={`block text-sm font-medium text-[#800020] mb-3 ${isBangla ? 'font-bangla' : ''}`}>
                        {t('additionalGuestsLabel')} *
                      </label>
                      <div className="space-y-3">
                        {formData.guest_names.map((name, index) => (
                          <div key={`guest-field-${index}`} className="flex gap-2">
                            <input
                              type="text"
                              value={name}
                              onChange={(e) => updateGuestName(index, e.target.value)}
                              placeholder={t('guestNamePlaceholder')}
                              className={`flex-1 px-4 py-3 min-h-[48px] rounded-xl border-2 border-[#E6C288] focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none transition-all bg-white/50 text-[#2D2424] placeholder-[#2D2424]/40 text-base ${isBangla ? 'font-bangla' : ''}`}
                              data-testid={`guest-name-${index}`}
                            />
                            {formData.guest_names.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeGuestField(index)}
                                className="p-3 min-w-[48px] min-h-[48px] rounded-xl border-2 border-red-200 text-red-500 hover:bg-red-50 transition-colors flex items-center justify-center"
                                data-testid={`remove-guest-${index}`}
                              >
                                <X className="w-5 h-5" />
                              </button>
                            )}
                          </div>
                        ))}
                        {formData.guest_names.length < 10 && (
                          <button
                            type="button"
                            onClick={addGuestField}
                            className={`w-full p-3 min-h-[48px] rounded-xl border-2 border-dashed border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/5 transition-colors flex items-center justify-center gap-2 ${isBangla ? 'font-bangla' : ''}`}
                            data-testid="add-guest-btn"
                          >
                            <UserPlus className="w-5 h-5" />
                            {t('addGuest')}
                          </button>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Notes */}
                <div>
                  <label 
                    htmlFor="notes" 
                    className={`block text-sm font-medium text-[#800020] mb-2 ${isBangla ? 'font-bangla' : ''}`}
                  >
                    {t('notesLabel')}
                  </label>
                  <textarea
                    id="notes"
                    data-testid="rsvp-notes"
                    rows={3}
                    maxLength={500}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder={t('notesPlaceholder')}
                    className={`w-full px-4 py-3 rounded-xl border-2 border-[#E6C288] focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none transition-all bg-white/50 text-[#2D2424] placeholder-[#2D2424]/40 resize-none text-base ${isBangla ? 'font-bangla leading-relaxed' : ''}`}
                  />
                </div>

                {/* Error Message */}
                <AnimatePresence>
                  {error && (
                    <motion.p 
                      className={`text-red-600 text-sm text-center p-3 bg-red-50 rounded-xl ${isBangla ? 'font-bangla' : ''}`}
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                    >
                      {error}
                    </motion.p>
                  )}
                </AnimatePresence>

                {/* Submit Button */}
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full py-4 min-h-[56px] rounded-xl bg-gradient-to-r from-[#800020] to-[#600018] text-white font-semibold text-base sm:text-lg flex items-center justify-center gap-3 hover:shadow-xl transition-all disabled:opacity-70 disabled:cursor-not-allowed ${isBangla ? 'font-bangla' : ''}`}
                  data-testid="rsvp-submit-btn"
                  whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                  whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {t('submitting')}
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      {t('submitRsvp')}
                    </>
                  )}
                </motion.button>
              </motion.form>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default RSVPSection;
